import { useQuery } from "@tanstack/react-query";
import { ExternalLink } from "lucide-react";

import { fetchCodexRule } from "@/lib/api";
import { CODEX_URL, navigateReviewLink } from "@/lib/review-navigation";
import { useStore } from "@/lib/store";

import { PageViewHeader } from "../PageViewHeader";
import { ReviewSectionTabs } from "./ReviewSectionTabs";

export function CodexRuleView({ ruleId }: { ruleId: string }): React.JSX.Element {
  const setView = useStore((state) => state.setView);
  const { data, isLoading, error } = useQuery({
    queryKey: ["codex-rule", ruleId],
    queryFn: () => fetchCodexRule(ruleId),
  });
  const rule = data?.rule;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <PageViewHeader
        title="PR Reviews"
        badge={
          <span className="bg-cyber-surface text-cyber-muted rounded-full px-1.5 py-0.5 font-mono text-[10px]">
            {ruleId}
          </span>
        }
        actions={<ReviewSectionTabs active="codex" />}
      />

      <main className="flex-1 overflow-y-auto px-4 py-8 md:px-8">
        <div className="mx-auto max-w-3xl">
          {isLoading ? (
            <div className="text-cyber-muted py-20 text-center text-sm">Loading rule...</div>
          ) : error || !rule ? (
            <div className="rounded border border-red-500/20 bg-red-500/5 p-5">
              <div className="font-mono text-sm text-red-400">{ruleId}</div>
              <p className="text-cyber-muted mt-2 text-sm">
                This Codex rule does not exist or is no longer available.
              </p>
            </div>
          ) : (
            <article className="space-y-6">
              <div>
                <a
                  href={CODEX_URL}
                  onClick={(event) =>
                    navigateReviewLink(event, CODEX_URL, "github-reviews-codex", setView)
                  }
                  className="text-cyber-muted hover:text-cyber-cyan mb-4 inline-flex text-xs transition-colors"
                >
                  Back to Codex
                </a>
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <h1 className="text-cyber-text font-mono text-xl font-semibold md:text-2xl">
                    {rule.id}
                  </h1>
                  <span className="bg-cyber-cyan/10 text-cyber-cyan rounded px-2 py-0.5 text-xs font-medium">
                    {rule.level}
                  </span>
                  <span className="bg-cyber-surface text-cyber-muted rounded px-2 py-0.5 text-xs">
                    {rule.status}
                  </span>
                </div>
                <div className="text-cyber-muted text-xs tracking-wider uppercase">
                  Revision {rule.revision} / {rule.domain} / Owned by {rule.owner}
                </div>
              </div>

              <section className="border-cyber-border bg-cyber-surface/30 rounded border p-5">
                <div className="text-cyber-muted mb-2 text-[10px] tracking-wider uppercase">
                  Rule
                </div>
                <p className="text-cyber-text text-base leading-7">{rule.rule}</p>
              </section>

              <section>
                <h2 className="text-cyber-muted mb-3 text-xs font-medium tracking-wider uppercase">
                  Repository scope
                </h2>
                <div className="space-y-3">
                  {rule.scopes.map((scope) => (
                    <div
                      key={`${scope.repository}:${scope.paths.join(",")}`}
                      className="border-cyber-border bg-cyber-surface/20 rounded border p-4"
                    >
                      <a
                        href={`https://github.com/${scope.repository}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-cyber-cyan inline-flex items-center gap-1.5 font-mono text-sm hover:underline"
                      >
                        {scope.repository}
                        <ExternalLink size={12} />
                      </a>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {scope.paths.map((path) => (
                          <code
                            key={path}
                            className="border-cyber-border bg-cyber-bg text-cyber-text rounded border px-2 py-1 text-xs"
                          >
                            {path}
                          </code>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <h2 className="text-cyber-muted mb-3 text-xs font-medium tracking-wider uppercase">
                  Exceptions
                </h2>
                {rule.exceptions.length === 0 ? (
                  <p className="text-cyber-muted text-sm">No documented exceptions.</p>
                ) : (
                  <ul className="text-cyber-text list-disc space-y-2 pl-5 text-sm">
                    {rule.exceptions.map((exception) => (
                      <li key={exception}>{exception}</li>
                    ))}
                  </ul>
                )}
              </section>
            </article>
          )}
        </div>
      </main>
    </div>
  );
}
